import React, { useContext, useEffect, useState } from 'react'
import { useProductStore } from '../../../../store/productStore';
import { Grid, useMediaQuery } from '@mui/material';
import GlobalPopUp from '../../../utility/globalPopUp';
import CreateProductPopup from './createProductPopup';
import { UserContext, UserContextPayload } from '../../../../context/userContext';
import globalStyles from "../../../../assets/styles/global.module.scss";
import ProductPageHeader from './productPageHeader';
import { EcomProduct } from '../../../../models/axiosModel';
import ProductTable from './productTable';
import { getProductTableFields, getWhatsAppCatalogDisplayLabel } from './productConstant';
import ProductFilterPopup from './productFilterPopup';
import GlobalDeletePopup from '../../../utility/globalDeletePopup/globalDeletePopup';
import agent from '../../../../services/api';
import { useShallow } from 'zustand/react/shallow';
import { useGenericStore } from '../../../../store/genericStore';

export interface filterProductInterface {
    product_name: EcomProduct[],
} 

export interface sortConfig {
    key: string,
    direction: string,
}

const Product = () => {
    
    const { setFlagLoading, flagLoading, userDetail, handleError } = useContext(UserContext) as UserContextPayload;

    const { flagCreateProductPopup, setFlagCreateProductPopup, getEComProductApi, eComProductData, catalogProductOptionalFieldData, getCatalogProductOptionalFieldApi, whatsAppCatalogsData, getWhatsAppCatalogsDataApi } = useProductStore(useShallow((state) => ({
        flagCreateProductPopup: state.flagCreateProductPopup,
        setFlagCreateProductPopup: state.setFlagCreateProductPopup,
        getEComProductApi: state.getEComProductApi,
        eComProductData: state.eComProductData,
        catalogProductOptionalFieldData: state.catalogProductOptionalFieldData,
        getCatalogProductOptionalFieldApi: state.getCatalogProductOptionalFieldApi,
        whatsAppCatalogsData: state.whatsAppCatalogsData,
        getWhatsAppCatalogsDataApi: state.getWhatsAppCatalogsDataApi,
    })))

    const { showSuccess, showError } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess,
        showError: state.showError
    })))

    const [filters, setFilters] = useState<filterProductInterface>({
        product_name: [],
    }); 
    const [sortConfig, setSortConfig] = useState<sortConfig>({ key: "", direction: '' });
    const [filteredProducts, setFilteredProducts] = useState<EcomProduct[]>([]);
    const [flagFilterPopup, setFlagFilterPopup] = useState<boolean>(false);
    const [checkedProducts, setCheckedProducts] = useState<EcomProduct[]>([]);

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<EcomProduct | null>(null);
    

    const breakPoints = {
        smallScreen: useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)'),
        flagTabletScreen: useMediaQuery<boolean>('(max-width : 900px) and (min-width : 600px)'),
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
        flagLaptopScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1550px)')
    }

    const getEComProductApiCall = async () => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true}));
        const res = await getEComProductApi(String(userDetail?.user_organization_data?.organization_id) ?? "");
        if(!(res.status)){
            handleError(res);
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false}));
    }  

    const getCatalogProductOptionalFieldApiCall = async () => {
        setFlagLoading((prev) => ({...prev, flagRoundLoader: true}));
        const res = await getCatalogProductOptionalFieldApi();
        if(!(res?.status)){
            handleError(res);
        }
        setFlagLoading((prev) => ({...prev, flagRoundLoader: false}));
    }

    const getWhatsAppCatalogsDataApiCall = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await getWhatsAppCatalogsDataApi();
        if (!(res?.status)) {
            handleError(res);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    const onClickOfRefresh = async () => {
        await getEComProductApiCall(); 
        setSortConfig({ key: "", direction: "" });
    } 

     const downloadCSVFile = () => {

        // If no data to download
        if (!filteredProducts || filteredProducts.length === 0) {
            showError("No data available to download.")
            return;
        }

        if (filteredProducts && filteredProducts.length > 0) {
            setFlagLoading({ ...flagLoading, flagRoundLoader: true });
            const hasWhatsAppCatalogsData = (whatsAppCatalogsData?.length ?? 0) > 0;
            const tableFields = getProductTableFields(hasWhatsAppCatalogsData);
            const headersSet = new Set<string>();
            tableFields.forEach(field => headersSet.add(field?.name))
            const headers = Array.from(headersSet);

            const rows = filteredProducts.map((product) => {
                const rows: string[] = [];
                headers.forEach(header => {
                    const field = tableFields?.find((pField) => pField?.name == header);
                    let fieldValue = product[(field?.getCallResponseKey) as keyof EcomProduct] ?? "";
                    if (field?.getCallResponseKey === "wa_catalog_id") {
                        fieldValue = getWhatsAppCatalogDisplayLabel(String(fieldValue ?? ""), whatsAppCatalogsData);
                    }
                    rows.push(String(fieldValue)); 
                })
                return rows;
            })

            const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `product_list.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setFlagLoading({ ...flagLoading, flagRoundLoader: false });
        }
    }

    useEffect(() => {
        getEComProductApiCall();
        if(process.env.REACT_APP_META_CATALOG_API_ENABLED == "true"){
            if(!catalogProductOptionalFieldData) {
                getCatalogProductOptionalFieldApiCall(); // Get Catalog Product Optional Field API
            }
            if (!whatsAppCatalogsData) {
                getWhatsAppCatalogsDataApiCall();
            }
        }
    }, []); 

    const deleteProductApi = async (selectedProduct: EcomProduct | null, checkedProducts: EcomProduct[]) => {
        if(selectedProduct || checkedProducts){
            setFlagLoading((prev) => ({ ... prev, flagRoundLoader: true }));
            const payload = {
                product_ids: selectedProduct ? 
                    [
                        {product_id: selectedProduct?.ecom_product_id}
                    ] 
                    : 
                    checkedProducts?.map((product) =>{
                        return {
                            product_id: product?.ecom_product_id,
                        }
                    }),
            }
            const res = await agent.deleteEComProduct.put(payload); 
            if(res.status){
                showSuccess("Product Deleted Successfully.")
                await getEComProductApiCall();
            } else {
                handleError(res);
            } 
            setShowDeleteConfirmation(false); 
            setCheckedProducts([]);
            setFlagLoading((prev) => ({ ... prev, flagRoundLoader: false }));
        }
    }

    return (
        <>
            <Grid container direction={"column"} gap={2}>
                <Grid item xs={12} className={globalStyles.fullwidth}>
                    <ProductPageHeader
                        filters={filters}
                        setFilters={setFilters}
                        filteredProducts={filteredProducts}
                        onClickOfRefresh={onClickOfRefresh} 
                        downloadCSVFile={downloadCSVFile} 
                        setFlagFilterPopup={setFlagFilterPopup} 
                        checkedProducts={checkedProducts} 
                        setCheckedProducts={setCheckedProducts} 
                        setShowDeleteConfirmation={setShowDeleteConfirmation} 
                    />
                </Grid> 
                <Grid item xs={12} className={globalStyles.fullwidth} mt={2}>
                    <ProductTable
                        filters={filters} 
                        sortConfig={sortConfig} 
                        setSortConfig={setSortConfig}
                        setFilteredProducts={setFilteredProducts}
                        getEComProductApiCall={getEComProductApiCall} 
                        checkedProducts={checkedProducts} 
                        setCheckedProducts={setCheckedProducts} 
                        showDeleteConfirmation={showDeleteConfirmation} 
                        setShowDeleteConfirmation={setShowDeleteConfirmation} 
                        selectedProduct={selectedProduct} 
                        setSelectedProduct={setSelectedProduct}
                        getWhatsAppCatalogsDataApiCall={getWhatsAppCatalogsDataApiCall}
                        getCatalogProductOptionalFieldApiCall={getCatalogProductOptionalFieldApiCall}
                    />
                </Grid>
            </Grid>
            {flagCreateProductPopup && 
                <GlobalPopUp
                    open={flagCreateProductPopup}
                    onClose={() => { setFlagCreateProductPopup(false); }}
                    handleClose={() => { setFlagCreateProductPopup(false); }}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "700px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "710px" : "800px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <CreateProductPopup
                        getEComProductApiCall={getEComProductApiCall}
                        getWhatsAppCatalogsDataApiCall={getWhatsAppCatalogsDataApiCall}
                        getCatalogProductOptionalFieldApiCall={getCatalogProductOptionalFieldApiCall}
                    />
                </GlobalPopUp>
            }

            {flagFilterPopup && 
                <GlobalPopUp
                    open={flagFilterPopup}
                    onClose={() => setFlagFilterPopup(false)}
                    handleClose={() => setFlagFilterPopup(false)}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.smallScreen ? "350px" : "600px"}
                    paperMaxwidth={breakPoints.smallScreen ? "610px" : "650px"}
                    borderRadius='10px'
                    titlePadding='0px'
                >
                    <ProductFilterPopup 
                        setFlagFilterPopup={setFlagFilterPopup} 
                        filters={filters}
                        setFilters={setFilters}
                    />
                </GlobalPopUp>
            } 

            {showDeleteConfirmation && 
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to delete ${selectedProduct ? selectedProduct?.ecom_product_name : checkedProducts?.length > 1 ? 'all selected products' : checkedProducts?.[0]?.ecom_product_name}`}
                    open={showDeleteConfirmation}
                    onClose={() => { setShowDeleteConfirmation(false); setSelectedProduct(null); }}
                    onDelete={() => { deleteProductApi(selectedProduct, checkedProducts); }}
                />
            } 
        </>
    )
}

export default Product
